import React, { useState } from 'react';
import { Menu, X, Home, ExternalLink, GraduationCap, Users, Headphones, Calendar, Bell, ChevronDown, User, Truck, Package, Trophy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUserDisplay } from '../hooks/useUserDisplay';
import { AuthModal, UserMenu } from './AuthComponents';
import NotificationsDropdown from './NotificationsDropdown';
import Logo from './Logo';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { SellerRevenueProgressBar } from './gamification/SellerRevenueProgressBar';
import { shouldRedirectToHome, navigateToSectionHome } from '../utils/navigationUtils';
import { useToast } from '../contexts/ToastContext';

interface TopbarProps {
  activeSection?: string;
  onSectionChange?: (section: string) => void;
  currentSubSection?: string;
  onSubSectionChange?: (subSection: string) => void;
  isAuthModalOpen: boolean;
  setIsAuthModalOpen: (isOpen: boolean) => void;
}

const Topbar: React.FC<TopbarProps> = ({ 
  activeSection, 
  onSectionChange,
  currentSubSection,
  onSubSectionChange,
  isAuthModalOpen,
  setIsAuthModalOpen
}) => {
  const { user, isAdmin, signOut } = useAuth();
  const { displayName, avatarInitial, avatarUrl, avatarKey, isLoggedIn, email } = useUserDisplay();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { showToast } = useToast();

  const menuItems = [
    { id: 'competicao', label: 'Competição', icon: Trophy },
    { id: 'home', label: 'Home', icon: Home },
    { id: 'mentorias', label: 'Mentorias', icon: GraduationCap },
    { id: 'useful-links', label: 'Ferramentas & Links', icon: ExternalLink },
    { id: 'suppliers', label: 'Fornecedores', icon: Truck },
    { id: 'calendar', label: 'Calendário', icon: Calendar },
    { id: 'community', label: 'Comunidade', icon: Users },
    { id: 'support', label: 'Suporte', icon: Headphones },
  ];

  const handleLogoClick = () => {
    if (onSectionChange) {
      onSectionChange('home');
    }
  };

  const handleBrandClick = () => {
    if (onSectionChange) {
      onSectionChange('home');
    }
  };

  const handleMobileMenuClick = (sectionId: string) => {
    // Verificar se precisa de login para acessar a comunidade
    if (sectionId === 'community' && !isLoggedIn) {
      setIsMobileMenuOpen(false);
      setIsAuthModalOpen(true);
      return;
    }

    // Verificar se deve redirecionar para a página inicial da seção
    if (shouldRedirectToHome(activeSection || '', sectionId, currentSubSection)) {
      navigateToSectionHome(sectionId, onSectionChange || (() => {}), onSubSectionChange);
    } else if (onSectionChange) {
      onSectionChange(sectionId);
    }
    
    setIsMobileMenuOpen(false);
  };

  const handleSectionChange = (sectionId: string) => {
    // Verificar se precisa de login para acessar a comunidade
    if (sectionId === 'community' && !isLoggedIn) {
      setIsAuthModalOpen(true);
      return;
    }

    // Verificar se deve redirecionar para a página inicial da seção
    if (shouldRedirectToHome(activeSection || '', sectionId, currentSubSection)) {
      navigateToSectionHome(sectionId, onSectionChange || (() => {}), onSubSectionChange);
    } else if (onSectionChange) {
      onSectionChange(sectionId);
    }
  };

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#121316] border-b border-[#1f2329] px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between max-w-screen-2xl mx-auto">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <Logo 
              size="md" 
              clickable={true}
              onClick={handleLogoClick}
            />
            <div 
              className="cursor-pointer hover:opacity-80 transition-opacity duration-200"
              onClick={handleBrandClick}
            >
               <h1 className="text-white font-extrabold text-xl tracking-wider uppercase">
                 <span className="font-light">Master</span><span className="text-[#0FFF55] font-black">Club</span>
               </h1>
               <div className="flex items-center space-x-2">
                 <div className="w-2 h-2 bg-[#0FFF55] rounded-full"></div>
                 <span className="text-gray-400 text-xs font-light tracking-wide uppercase">Brasil</span>
               </div>
            </div>
          </div>

          {/* Desktop - Notifications, User Menu and Fixed Revenue Progress */}
          <div className="hidden md:flex items-center space-x-3 lg:space-x-4">
            {/* Admin Panel Button - Only for admins */}
            {isAdmin && (
              <button
                onClick={() => handleSectionChange('admin')}
                className="flex items-center space-x-2 bg-[#0FFF55]/10 text-[#0FFF55] px-3.5 py-2 rounded-xl hover:bg-[#0FFF55]/20 transition-colors border border-[#0FFF55]/20"
              >
                <span className="text-sm font-semibold">Admin</span>
              </button>
            )}

            {/* Notifications */}
            <NotificationsDropdown 
              onNavigate={(section, subSection) => {
                if (onSectionChange) onSectionChange(section);
                if (onSubSectionChange && subSection) onSubSectionChange(subSection);
              }}
            />

            {/* User Menu */}
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center space-x-3 p-2 hover:bg-[#1a1d23] rounded-xl transition-colors"
            >
              {isLoggedIn ? (
                <>
                  <Avatar className="w-8 h-8 ring-2 ring-[#0FFF55]/30" key={avatarKey}>
                    <AvatarImage src={avatarUrl} key={`${avatarUrl}-${avatarKey}`} />
                    <AvatarFallback className="bg-gradient-to-br from-[#0FFF55] to-[#0dd149] text-[#121316] font-bold text-sm">
                      {avatarInitial}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-white text-sm font-medium">
                    Olá, {displayName}!
                  </span>
                </>
              ) : (
                <>
                  <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                    <User className="text-gray-400" size={16} />
                  </div>
                  <span className="text-gray-400 text-sm">Olá, Convidado!</span>
                </>
              )}
              <ChevronDown className="text-gray-400" size={16} />
            </button>

            {/* Barra de Progresso de Faturamento Fixa na Topbar (do lado direito de Olá, Vítor!) */}
            {isLoggedIn && user && (
              <SellerRevenueProgressBar
                userId={user.id}
                variant="topbar"
                onOpenIntegrations={() => handleSectionChange('integrations')}
              />
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2 rounded-lg hover:bg-[#1a1d23] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="absolute top-20 left-0 right-0 bg-[#0f1114] border-b border-[#1f2329] p-4">
            {/* Mobile User Info */}
            <div className="mb-4 p-4 bg-[#1a1d21] rounded-xl space-y-3">
              {isLoggedIn ? (
                <div className="flex flex-col space-y-3">
                  <div className="flex items-center space-x-3">
                    <Avatar className="w-10 h-10 ring-2 ring-[#0FFF55]/30" key={avatarKey}>
                      <AvatarImage src={avatarUrl} key={`${avatarUrl}-${avatarKey}`} />
                      <AvatarFallback className="bg-gradient-to-br from-[#0FFF55] to-[#0dd149] text-[#121316] font-bold text-sm">
                        {avatarInitial}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold truncate">
                        Olá, {displayName}!
                      </p>
                      <p className="text-gray-400 text-xs truncate">{email}</p>
                      {isAdmin && (
                        <span className="inline-block bg-[#0FFF55] text-[#121316] text-[10px] px-2 py-0.5 rounded-full font-bold mt-1">
                          ADMIN
                        </span>
                      )}
                    </div>
                  </div>

                  {user && (
                    <SellerRevenueProgressBar
                      userId={user.id}
                      variant="topbar"
                      onOpenIntegrations={() => {
                        setIsMobileMenuOpen(false);
                        handleSectionChange('integrations');
                      }}
                    />
                  )}

                  <div className="border-t border-gray-700/50 pt-2 flex justify-end">
                    <button
                      onClick={async () => {
                        setIsMobileMenuOpen(false);
                        await signOut();
                      }}
                      className="text-red-400 hover:text-red-300 text-sm font-medium flex items-center space-x-1 py-1 px-3 rounded-lg hover:bg-red-500/10 transition-colors"
                    >
                      <span>Sair da conta</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-2">
                    <User className="text-gray-400" size={20} />
                  </div>
                  <p className="text-white font-semibold">Olá, Convidado!</p>
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setIsAuthModalOpen(true);
                    }}
                    className="mt-2 bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] font-bold py-2 px-4 rounded-xl text-sm"
                  >
                    Fazer Login
                  </button>
                </div>
              )}
            </div>

            <nav>
              <ul className="space-y-2">
                {menuItems.map(({ id, label, icon: Icon }) => {
                  const isAuthRequired = ['community'].includes(id);
                  const isCompetition = id === 'competicao';
                  return (
                    <li key={id}>
                      <button
                        onClick={() => handleMobileMenuClick(id)}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 text-left ${
                          activeSection === id
                            ? 'bg-[#0FFF55] text-[#0f1114] font-bold shadow-[0_0_15px_rgba(15,255,85,0.6)]'
                            : isCompetition
                            ? 'text-[#0FFF55] bg-[#0FFF55]/10 border border-[#0FFF55]/30 shadow-[0_0_10px_rgba(15,255,85,0.2)]'
                            : 'text-white hover:bg-[#1a1d23]'
                        } ${isAuthRequired && !isLoggedIn ? 'opacity-50' : ''}`}
                      >
                        <Icon size={20} className={isCompetition && activeSection !== id ? 'text-[#0FFF55] animate-pulse drop-shadow-[0_0_8px_#0FFF55]' : ''} />
                        <span className={`font-medium flex items-center justify-between flex-1 ${isCompetition && activeSection !== id ? 'text-[#0FFF55] font-bold' : ''}`}>
                          <span>{label}</span>
                          {isCompetition && (
                            <span className="text-[9px] bg-[#0FFF55]/20 text-[#0FFF55] px-1.5 py-0.5 rounded font-black tracking-wider uppercase border border-[#0FFF55]/40 ml-1.5">
                              LIVE
                            </span>
                          )}
                          {isAuthRequired && !isLoggedIn && (
                            <span className="text-xs ml-2 text-gray-500">(Login necessário)</span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
                
                {/* Admin Panel - Mobile */}
                {isAdmin && (
                  <li>
                    <button
                      onClick={() => handleMobileMenuClick('admin')}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 text-left ${
                        activeSection === 'admin'
                          ? 'bg-[#0FFF55] text-[#0f1114]'
                          : 'text-[#0FFF55] hover:bg-[#0FFF55]/10 border border-[#0FFF55]/20'
                      }`}
                    >
                      <span className="font-medium">Painel Admin</span>
                    </button>
                  </li>
                )}
              </ul>
            </nav>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
      />

      {/* User Menu */}
      <UserMenu
        isOpen={isUserMenuOpen}
        onClose={() => setIsUserMenuOpen(false)}
        onOpenAuth={() => setIsAuthModalOpen(true)}
        onNavigate={handleSectionChange}
      />
    </>
  );
};

export default Topbar;